<script setup lang="ts">
import type { Competitor } from '~~/shared/compare/types'
import { COMPARE_GROUPS, COMPARE_ROWS } from '~~/shared/compare/rows'

// Feature-by-feature table: mychar.gg vs one competitor. A real <table> (not a
// grid of divs) so crawlers and AI answer engines can read rows as facts.
// Only rows the competitor data fills in are shown.
const props = defineProps<{
  competitor: Competitor
}>()

const groups = computed(() => COMPARE_GROUPS
  .map(group => ({
    ...group,
    rows: COMPARE_ROWS.filter(row => row.group === group.id && props.competitor.features[row.id] !== undefined)
  }))
  .filter(group => group.rows.length > 0))
</script>

<template>
  <div class="min-w-0 overflow-x-auto rounded-2xl border border-default bg-elevated/40 shadow-(--surface-shadow)">
    <table class="w-full table-fixed text-left text-xs sm:text-sm">
      <caption class="sr-only">
        mychar.gg compared with {{ competitor.name }}: features and pricing
      </caption>
      <thead>
        <tr class="border-b border-default">
          <th
            scope="col"
            class="w-[36%] px-3 py-3.5 font-medium text-muted sm:w-2/5 sm:px-6"
          >
            Feature
          </th>
          <th
            scope="col"
            class="px-3 py-3.5 font-semibold text-highlighted sm:px-6"
          >
            mychar.gg
          </th>
          <th
            scope="col"
            class="px-3 py-3.5 font-semibold text-highlighted sm:px-6"
          >
            {{ competitor.name }}
          </th>
        </tr>
      </thead>
      <tbody
        v-for="group in groups"
        :key="group.id"
      >
        <tr class="border-t border-default bg-muted/40">
          <th
            colspan="3"
            scope="colgroup"
            class="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-dimmed sm:px-6"
          >
            {{ group.label }}
          </th>
        </tr>
        <tr
          v-for="row in group.rows"
          :key="row.id"
          class="border-t border-default align-top"
        >
          <th
            scope="row"
            class="px-3 py-3 font-normal text-toned sm:px-6"
          >
            {{ row.label }}
          </th>
          <td class="px-3 py-3 sm:px-6">
            <CompareCellValue :cell="row.mychar" />
          </td>
          <td class="px-3 py-3 sm:px-6">
            <CompareCellValue :cell="competitor.features[row.id]" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
